import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useStore } from './store/useStore';
import { PLAN_LIMITS, PlanTier } from './types';
import PricingModal from './components/PricingModal';
import OnboardingModal from './components/OnboardingModal';
import { LogIn, LogOut, AlertCircle, User as UserIcon, Loader2, Globe, Zap, ChevronRight, Command, Sparkles, Palette, LineChart, Cpu, Check, Search, Send, Image as ImageIcon, Layout, TrendingUp, DollarSign } from 'lucide-react';

// Lazy Load Components
const Shelf = lazy(() => import('./components/Shelf'));
const Explore = lazy(() => import('./components/Explore'));
const IdeaEditor = lazy(() => import('./components/IdeaEditor'));
const ComparisonView = lazy(() => import('./components/ComparisonView'));

// Lazy load Legal Pages to match Explore behavior
const TermsPage = lazy(() => import('./components/LegalPages').then(module => ({ default: module.TermsPage })));
const PrivacyPage = lazy(() => import('./components/LegalPages').then(module => ({ default: module.PrivacyPage })));
const SecurityPage = lazy(() => import('./components/LegalPages').then(module => ({ default: module.SecurityPage })));

// --- Navigation Header Component ---
const Header: React.FC = () => {
    const { user, userProfile, logout, isSyncing, setPricingOpen } = useStore();
    const location = useLocation();
    
    // Don't show header on home page if not logged in
    if (!user && !useStore.getState().isGuest && location.pathname === '/') return null;

    // Explicitly type the plan to avoid 'any' indexing error
    const currentPlan: PlanTier = (userProfile?.plan as PlanTier) || 'free';
    const limits = PLAN_LIMITS[currentPlan];

    return (
        <header className="fixed top-0 left-0 right-0 h-16 border-b border-white/5 bg-black/80 backdrop-blur z-40 flex items-center px-6 justify-between animate-in fade-in duration-300">
            <div className="flex items-center gap-8">
                <Link to="/" className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center font-bold text-sm shadow-lg">IS</div>
                    <h1 className="font-semibold text-sm tracking-tight text-white hidden sm:block">Idea Shelf</h1>
                </Link>

                <nav className="flex items-center gap-1">
                    <Link to="/" className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${location.pathname === '/' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>My Shelf</Link>
                    <Link to="/explore" className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-2 ${location.pathname === '/explore' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}><Globe size={14} /> Explore</Link>
                </nav>
            </div>

            <div className="flex items-center gap-4">
                    {userProfile && limits && (
                    <div 
                        className="hidden md:flex items-center gap-2 text-xs bg-white/5 px-3 py-1.5 rounded-full border border-white/5 cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => setPricingOpen(true)}
                    >
                        <Zap size={12} className={userProfile.tokensUsed > limits.credits * 0.9 ? "text-red-400" : "text-yellow-400"} />
                        <span className="text-zinc-400">
                            {userProfile.tokensUsed.toLocaleString()} / <span className="text-zinc-500">{limits.credits.toLocaleString()}</span>
                        </span>
                        {userProfile.plan === 'free' && (
                            <span className="ml-2 px-1.5 py-0.5 bg-indigo-500 text-white rounded text-[10px] font-bold">UPGRADE</span>
                        )}
                    </div>
                    )}
                    
                    <div className="h-6 w-px bg-white/10 mx-2 hidden sm:block"></div>
                    <div className="flex items-center gap-3">
                    {isSyncing && (
                            <div className="flex items-center gap-2 text-xs text-indigo-400 bg-indigo-500/10 px-3 py-1.5 rounded-full border border-indigo-500/20 mr-2 animate-pulse">
                            <Loader2 size={12} className="animate-spin" />
                            <span className="hidden sm:inline">Syncing...</span>
                        </div>
                    )}
                    {user ? (
                        <img src={user.photoURL || ''} alt="User" className="w-8 h-8 rounded-full border border-white/10 shadow-sm" />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-zinc-800 border border-white/10 flex items-center justify-center">
                            <UserIcon size={14} className="text-zinc-400" />
                        </div>
                    )}
                    <button onClick={logout} className="p-2 hover:bg-white/5 rounded-full text-zinc-400 hover:text-white transition-colors">
                        <LogOut size={16} />
                    </button>
                    </div>
            </div>
        </header>
    );
};

// --- Landing Page Component ---
const LandingPage: React.FC = () => {
    const { login, enterGuestMode, authError } = useStore();
    const [demoStep, setDemoStep] = useState(0);

    // Animation Loop for Demo
    useEffect(() => {
        const interval = setInterval(() => {
            setDemoStep((prev) => (prev + 1) % 6);
        }, 3500);
        return () => clearInterval(interval);
    }, []);

    const demoSteps = [
        { title: "Capture Idea", desc: "Just describe it." },
        { title: "AI Research", desc: "Agents analyze market." },
        { title: "Generate Assets", desc: "Logos & Concepts." },
        { title: "Strategy", desc: "Deep business plan." },
        { title: "Track Growth", desc: "Live metrics." },
        { title: "Pitch Investors", desc: "Deck ready." },
    ];

    return (
        <div className="min-h-dvh bg-black text-zinc-300 font-sans selection:bg-indigo-500/30 overflow-x-hidden scroll-smooth relative">
              {/* Grid Background */}
              <div 
                className="fixed inset-0 z-0 pointer-events-none" 
                style={{
                    backgroundImage: `linear-gradient(to right, #ffffff05 1px, transparent 1px), linear-gradient(to bottom, #ffffff05 1px, transparent 1px)`,
                    backgroundSize: '40px 40px',
                    WebkitMaskImage: 'radial-gradient(circle at center, black, transparent 80%)',
                    maskImage: 'radial-gradient(circle at center, black, transparent 80%)'
                }} 
              />
              
              <nav className="fixed w-full z-50 px-6 py-4 flex justify-between items-center bg-black/50 backdrop-blur-md border-b border-white/5 top-0 left-0">
                  <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-md bg-white text-black flex items-center justify-center font-bold text-xs shadow-[0_0_15px_rgba(255,255,255,0.3)]">IS</div>
                      <span className="font-semibold text-sm tracking-tight text-white">Idea Shelf</span>
                  </div>
                  <div className="hidden md:flex items-center gap-8 text-xs font-medium text-zinc-400">
                      <a href="#features" className="hover:text-white transition-colors">Features</a>
                      <a href="#manifesto" className="hover:text-white transition-colors">Manifesto</a>
                      <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
                  </div>
                  <div className="flex items-center gap-4">
                      <button onClick={login} className="text-xs font-medium text-zinc-300 hover:text-white transition-colors">Log in</button>
                      <button onClick={login} className="px-3 py-1.5 text-xs font-medium bg-white text-black rounded-full hover:bg-zinc-200 transition-colors">Sign up</button>
                  </div>
              </nav>

              <div className="relative pt-32 pb-20 px-6 flex flex-col items-center text-center z-10 w-full">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-indigo-600/20 rounded-[100%] blur-[120px] pointer-events-none opacity-40 mix-blend-screen" />
                  
                  <div className="relative z-10 space-y-8 max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-1000">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-wider font-bold text-indigo-300 hover:bg-white/10 transition-colors cursor-default">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                          </span>
                          Idea Shelf 2.0 is live
                          <ChevronRight size={12} className="opacity-50" />
                      </div>
                      
                      {/* HERO TYPE */}
                      <h1 className="text-6xl md:text-8xl font-bold tracking-tight text-white leading-[1.1] max-w-4xl mx-auto">
                          The OS for your <br />
                          <span className="text-transparent bg-clip-text bg-gradient-to-br from-zinc-200 via-zinc-400 to-zinc-600">product ideas.</span>
                      </h1>
                      
                      {/* SUBTITLE */}
                      <p className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed">
                          Turn napkin sketches into Sequoia-ready pitch decks in seconds. 
                          Research markets, generate visuals, and validate strategies with AI.
                      </p>

                      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                          <button onClick={login} className="h-12 px-8 rounded-full bg-white text-black font-medium text-sm hover:bg-zinc-200 transition-all flex items-center gap-2 group shadow-[0_0_20px_rgba(255,255,255,0.2)] hover:shadow-[0_0_30px_rgba(255,255,255,0.4)]">
                              Start Building <ChevronRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                          </button>
                          <button onClick={enterGuestMode} className="h-12 px-8 rounded-full bg-white/5 text-white font-medium text-sm hover:bg-white/10 border border-white/5 transition-all flex items-center gap-2 backdrop-blur-sm">
                              <Command size={16} className="opacity-50" /> Guest Mode
                          </button>
                      </div>

                      {authError && (
                          <div className="mt-8 p-3 bg-red-900/20 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-xs max-w-md mx-auto">
                              <AlertCircle size={14} /> {authError}
                          </div>
                      )}
                  </div>
                  
                  {/* App Preview Animation */}
                  <div className="relative w-full max-w-6xl mx-auto mt-24 [perspective:2000px] group z-20">
                      <div className="relative rounded-xl bg-[#0A0A0A] border border-white/10 shadow-2xl overflow-hidden transform [transform:rotateX(6deg)] group-hover:[transform:rotateX(0deg)] transition-transform duration-1000 ease-out origin-center min-h-[600px] flex flex-col">
                          {/* Window Chrome */}
                          <div className="h-10 border-b border-white/5 bg-black/50 flex items-center px-4 gap-2 shrink-0">
                              <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/30" /><div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/30" /><div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/30" /></div>
                              <div className="mx-auto w-64 h-5 rounded bg-white/5 flex items-center justify-center text-[10px] text-zinc-600 font-mono">ideashelf.app</div>
                          </div>

                          {/* Dynamic Content Area */}
                          <div className="flex-1 bg-gradient-to-b from-[#09090b] to-black relative overflow-hidden">
                              {/* Background Glow */}
                              <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-indigo-500/5 blur-3xl rounded-full" />

                              {/* --- STEP 1: CAPTURE --- */}
                              {demoStep === 0 && (
                                  <div className="absolute inset-0 flex items-center justify-center p-8 animate-in fade-in zoom-in-95 duration-500">
                                      <div className="w-full max-w-xl">
                                          <div className="text-sm font-medium text-zinc-500 mb-4">What do you want to build?</div>
                                          <div className="text-4xl md:text-5xl font-bold text-white mb-2 leading-tight">
                                              A Spotify for <br />
                                              <span className="text-indigo-400 animate-pulse">Cat Food.</span>
                                          </div>
                                          <div className="mt-8 flex gap-4">
                                              <div className="h-12 w-full bg-zinc-900 rounded-xl border border-white/10 flex items-center px-4 text-zinc-600">
                                                  Describe features...
                                              </div>
                                              <div className="h-12 w-12 bg-white rounded-xl flex items-center justify-center text-black">
                                                  <Send size={20} />
                                              </div>
                                          </div>
                                      </div>
                                  </div>
                              )}

                              {/* --- STEP 2: RESEARCH --- */}
                              {demoStep === 1 && (
                                  <div className="absolute inset-0 flex items-center justify-center p-8 animate-in fade-in duration-500">
                                      <div className="grid grid-cols-2 gap-4 w-full max-w-3xl">
                                          <div className="bg-zinc-900/50 border border-white/10 p-6 rounded-2xl space-y-4">
                                              <div className="flex items-center gap-3 text-indigo-400 text-sm font-bold"><Loader2 className="animate-spin" size={16} /> Market Research Agent</div>
                                              <div className="space-y-2">
                                                  <div className="h-2 w-3/4 bg-white/10 rounded-full animate-pulse" />
                                                  <div className="h-2 w-1/2 bg-white/10 rounded-full animate-pulse" />
                                                  <div className="h-2 w-5/6 bg-white/10 rounded-full animate-pulse" />
                                              </div>
                                          </div>
                                          <div className="bg-zinc-900/50 border border-white/10 p-6 rounded-2xl space-y-4 opacity-75">
                                              <div className="flex items-center gap-3 text-emerald-400 text-sm font-bold"><Check size={16} /> Competitor Analysis</div>
                                              <div className="flex gap-2">
                                                  <div className="px-3 py-1 rounded-full bg-white/5 text-[10px] text-zinc-500">Chewy</div>
                                                  <div className="px-3 py-1 rounded-full bg-white/5 text-[10px] text-zinc-500">CatPerson</div>
                                              </div>
                                          </div>
                                          <div className="col-span-2 bg-zinc-900/50 border border-white/10 p-6 rounded-2xl flex items-center justify-between">
                                               <div>
                                                   <div className="text-xs text-zinc-500 uppercase font-bold">Total Addressable Market</div>
                                                   <div className="text-3xl font-bold text-white mt-1">$4.2B</div>
                                               </div>
                                               <div className="h-12 w-12 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400"><TrendingUp size={24} /></div>
                                          </div>
                                      </div>
                                  </div>
                              )}

                              {/* --- STEP 3: VISUALS (ENHANCED) --- */}
                              {demoStep === 2 && (
                                  <div className="absolute inset-0 flex items-center justify-center p-8 animate-in fade-in duration-500">
                                      <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-6 h-full max-h-[400px]">
                                          
                                          {/* Left: Brand Identity / Logo */}
                                          <div className="bg-zinc-900/80 border border-white/10 rounded-2xl p-6 flex flex-col relative overflow-hidden backdrop-blur-sm">
                                               <div className="flex justify-between items-center mb-6">
                                                   <div className="flex items-center gap-2">
                                                       <Palette size={14} className="text-indigo-400" />
                                                       <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Identity System</span>
                                                   </div>
                                                   <div className="px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 text-[10px] border border-indigo-500/20 flex items-center gap-1">
                                                       <Sparkles size={8} /> Generated
                                                   </div>
                                               </div>
                                               
                                               <div className="flex-1 flex items-center justify-center gap-6">
                                                   {/* Primary Logo Mock */}
                                                   <div className="relative group">
                                                       <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-700 shadow-[0_0_40px_rgba(79,70,229,0.3)] flex items-center justify-center text-white text-5xl font-bold relative z-10 border border-white/10">
                                                           P
                                                       </div>
                                                       {/* Decorative Elements behind logo */}
                                                       <div className="absolute -inset-4 bg-indigo-500/20 blur-xl rounded-full opacity-50" />
                                                   </div>

                                                   {/* Color Palette Swatches */}
                                                   <div className="flex flex-col gap-3">
                                                       <div className="w-10 h-10 rounded-lg bg-[#4f46e5] border border-white/10 shadow-lg transform -rotate-6 transition-transform group-hover:-rotate-12" />
                                                       <div className="w-10 h-10 rounded-lg bg-[#18181b] border border-white/10 shadow-lg transform rotate-3 transition-transform group-hover:rotate-6" />
                                                       <div className="w-10 h-10 rounded-lg bg-[#f4f4f5] border border-white/10 shadow-lg transform -rotate-3 transition-transform group-hover:-rotate-6" />
                                                   </div>
                                               </div>
                                               <div className="mt-6 space-y-2">
                                                   <div className="h-2 w-24 bg-zinc-800 rounded-full" />
                                                   <div className="h-2 w-32 bg-zinc-800 rounded-full opacity-50" />
                                               </div>
                                          </div>

                                          {/* Right: Landing Page Wireframe with Shimmer */}
                                          <div className="bg-zinc-900/80 border border-white/10 rounded-2xl p-6 flex flex-col relative overflow-hidden backdrop-blur-sm">
                                               
                                               {/* Shimmer Overlay */}
                                               <div className="absolute inset-0 z-20 pointer-events-none bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-shimmer" />
                                               
                                               <div className="flex justify-between items-center mb-6 relative z-10">
                                                   <div className="flex items-center gap-2">
                                                       <Layout size={14} className="text-emerald-400" />
                                                       <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Web Concept</span>
                                                   </div>
                                                   <div className="flex gap-1.5">
                                                       <div className="w-2 h-2 rounded-full bg-red-500/20" />
                                                       <div className="w-2 h-2 rounded-full bg-yellow-500/20" />
                                                       <div className="w-2 h-2 rounded-full bg-green-500/20" />
                                                   </div>
                                               </div>

                                               {/* Wireframe UI */}
                                               <div className="flex-1 bg-black/40 border border-white/5 rounded-lg p-4 relative z-0 flex flex-col gap-4 overflow-hidden">
                                                    {/* Nav */}
                                                    <div className="flex justify-between items-center">
                                                        <div className="w-6 h-6 rounded bg-zinc-800" />
                                                        <div className="flex gap-2">
                                                            <div className="w-12 h-2 bg-zinc-800 rounded-full" />
                                                            <div className="w-12 h-2 bg-zinc-800 rounded-full" />
                                                        </div>
                                                    </div>

                                                    {/* Hero Section */}
                                                    <div className="flex items-center gap-4 mt-2">
                                                        <div className="flex-1 space-y-3">
                                                            <div className="w-3/4 h-6 bg-zinc-700 rounded-md" />
                                                            <div className="w-1/2 h-6 bg-zinc-700 rounded-md" />
                                                            <div className="w-full h-16 bg-zinc-800/50 rounded-md mt-2" />
                                                            <div className="flex gap-2 mt-2">
                                                                <div className="w-20 h-6 bg-indigo-500/30 rounded-md" />
                                                                <div className="w-20 h-6 bg-zinc-800 rounded-md" />
                                                            </div>
                                                        </div>
                                                        <div className="w-24 h-32 bg-zinc-800 rounded-lg border border-white/5 flex items-center justify-center">
                                                            <ImageIcon size={20} className="text-zinc-600" />
                                                        </div>
                                                    </div>

                                                    {/* Grid Section */}
                                                    <div className="grid grid-cols-3 gap-2 mt-auto">
                                                        <div className="aspect-square rounded bg-zinc-800/50" />
                                                        <div className="aspect-square rounded bg-zinc-800/50" />
                                                        <div className="aspect-square rounded bg-zinc-800/50" />
                                                    </div>
                                               </div>
                                          </div>

                                      </div>
                                  </div>
                              )}

                              {/* --- STEP 4: STRATEGY --- */}
                              {demoStep === 3 && (
                                  <div className="absolute inset-0 p-8 flex animate-in fade-in slide-in-from-right-8 duration-500">
                                      <div className="w-64 border-r border-white/5 pr-6 space-y-2 hidden md:block">
                                          <div className="h-8 bg-indigo-500/10 border border-indigo-500/20 rounded-lg w-full flex items-center px-3 text-xs text-indigo-300 gap-2"><Sparkles size={12} /> Strategy</div>
                                          <div className="h-8 rounded-lg w-full flex items-center px-3 text-xs text-zinc-500 gap-2"><Layout size={12} /> Landing Page</div>
                                          <div className="h-8 rounded-lg w-full flex items-center px-3 text-xs text-zinc-500 gap-2"><DollarSign size={12} /> Pricing</div>
                                      </div>
                                      <div className="flex-1 pl-8 space-y-6">
                                          <div className="flex justify-between items-start">
                                              <div>
                                                  <div className="text-3xl font-bold text-white">Purrfect Plate</div>
                                                  <div className="text-sm text-zinc-500">SaaS • B2C • Subscription</div>
                                              </div>
                                              <div className="flex gap-2">
                                                  <div className="px-3 py-1 bg-green-500/10 text-green-400 border border-green-500/20 rounded-full text-xs">Validated</div>
                                              </div>
                                          </div>
                                          <div className="grid grid-cols-3 gap-4">
                                              <div className="bg-zinc-900 p-4 rounded-xl border border-white/5 space-y-2">
                                                  <div className="text-[10px] uppercase text-zinc-500 font-bold">Acquisition</div>
                                                  <div className="text-sm text-zinc-300">SEO + TikTok Ads</div>
                                              </div>
                                              <div className="bg-zinc-900 p-4 rounded-xl border border-white/5 space-y-2">
                                                  <div className="text-[10px] uppercase text-zinc-500 font-bold">Revenue</div>
                                                  <div className="text-sm text-zinc-300">$15/mo Sub</div>
                                              </div>
                                              <div className="bg-zinc-900 p-4 rounded-xl border border-white/5 space-y-2">
                                                  <div className="text-[10px] uppercase text-zinc-500 font-bold">Launch</div>
                                                  <div className="text-sm text-zinc-300">3 Months</div>
                                              </div>
                                          </div>
                                      </div>
                                  </div>
                              )}

                              {/* --- STEP 5: TRACTION --- */}
                              {demoStep === 4 && (
                                  <div className="absolute inset-0 flex items-center justify-center p-8 animate-in fade-in duration-500">
                                      <div className="w-full max-w-3xl bg-zinc-900/50 border border-white/10 rounded-2xl p-8 relative overflow-hidden">
                                          <div className="flex justify-between mb-8">
                                              <div>
                                                  <div className="text-sm text-zinc-500 uppercase tracking-widest font-bold">Monthly Recurring Revenue</div>
                                                  <div className="text-4xl font-bold text-white mt-2">$12,450</div>
                                              </div>
                                              <div className="px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-xs h-fit font-bold">+24%</div>
                                          </div>
                                          <div className="h-32 flex items-end gap-2">
                                              {[40, 55, 45, 70, 85, 95, 120, 140, 135, 160].map((h: number, i: number) => (
                                                  <div key={i} className="flex-1 bg-indigo-500/20 hover:bg-indigo-500 transition-colors rounded-t-sm relative group" style={{ height: `${h}%` }}>
                                                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-white text-black text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">${h * 100}</div>
                                                  </div>
                                              ))}
                                          </div>
                                      </div>
                                  </div>
                              )}

                              {/* --- STEP 6: PITCH --- */}
                              {demoStep === 5 && (
                                  <div className="absolute inset-0 flex items-center justify-center p-8 animate-in fade-in zoom-in-95 duration-500">
                                      <div className="aspect-video w-full max-w-3xl bg-white text-black rounded-xl p-8 md:p-12 flex flex-col justify-between shadow-2xl relative overflow-hidden">
                                          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
                                          <div className="flex justify-between items-start z-10">
                                               <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-bold text-sm rounded-lg">IS</div>
                                                    <span className="font-semibold tracking-tight text-lg">Purrfect Plate</span>
                                               </div>
                                               <div className="text-[10px] font-bold uppercase tracking-widest border border-black/10 px-3 py-1 rounded-full text-zinc-500">Series A Round</div>
                                          </div>
                                          <div className="flex-1 flex gap-8 z-10 mt-6 relative items-center">
                                              <div className="w-1/2 flex flex-col justify-center space-y-6">
                                                  <h2 className="text-4xl font-bold tracking-tight leading-none">Scale to <br/><span className="text-indigo-600">$100M ARR</span></h2>
                                                  <div className="space-y-4">
                                                      <div className="flex items-center gap-4 group cursor-pointer"><div className="w-10 h-10 rounded-full bg-indigo-50 group-hover:bg-indigo-600 transition-colors flex items-center justify-center text-indigo-600 group-hover:text-white font-bold text-sm shadow-sm">1</div><p className="text-sm font-medium text-zinc-600 group-hover:text-black transition-colors">Viral Loop Acquisition</p></div>
                                                      <div className="flex items-center gap-4 group cursor-pointer"><div className="w-10 h-10 rounded-full bg-indigo-50 group-hover:bg-indigo-600 transition-colors flex items-center justify-center text-indigo-600 group-hover:text-white font-bold text-sm shadow-sm">2</div><p className="text-sm font-medium text-zinc-600 group-hover:text-black transition-colors">Enterprise Expansion</p></div>
                                                      <div className="flex items-center gap-4 group cursor-pointer"><div className="w-10 h-10 rounded-full bg-indigo-50 group-hover:bg-indigo-600 transition-colors flex items-center justify-center text-indigo-600 group-hover:text-white font-bold text-sm shadow-sm">3</div><p className="text-sm font-medium text-zinc-600 group-hover:text-black transition-colors">Global Dominance</p></div>
                                                  </div>
                                              </div>
                                              <div className="w-1/2 bg-zinc-50 rounded-2xl border border-zinc-100 p-6 relative overflow-hidden h-56 flex items-end shadow-inner">
                                                  <svg viewBox="0 0 200 100" className="w-full h-full overflow-visible"><defs><linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4f46e5" stopOpacity="0.2"/><stop offset="100%" stopColor="#4f46e5" stopOpacity="0"/></linearGradient></defs><line x1="0" y1="25" x2="200" y2="25" stroke="#e4e4e7" strokeWidth="1" /><line x1="0" y1="50" x2="200" y2="50" stroke="#e4e4e7" strokeWidth="1" /><line x1="0" y1="75" x2="200" y2="75" stroke="#e4e4e7" strokeWidth="1" /><path d="M0 90 Q 50 90, 80 60 T 200 10" fill="none" stroke="#4f46e5" strokeWidth="3" vectorEffect="non-scaling-stroke" /><path d="M0 90 Q 50 90, 80 60 T 200 10 V 100 H 0 Z" fill="url(#chartGradient)" /><circle cx="0" cy="90" r="3" fill="#4f46e5" /><circle cx="80" cy="60" r="3" fill="#4f46e5" /><circle cx="200" cy="10" r="3" fill="#4f46e5" /></svg>
                                                  <div className="absolute top-4 left-4 bg-white shadow-xl p-3 rounded-lg border border-zinc-100 animate-[bounce_3s_infinite]"><div className="text-[8px] text-zinc-400 uppercase font-bold tracking-wider">YoY Growth</div><div className="text-xl font-bold text-indigo-600 leading-none mt-1">+340%</div></div>
                                              </div>
                                          </div>
                                          <div className="flex justify-between items-center text-[10px] text-zinc-400 font-medium z-10 pt-4 border-t border-black/5"><div>Confidential Overview</div><div>2025</div></div>
                                      </div>
                                  </div>
                              )}
                          </div>

                          {/* Progress Indicator */}
                          <div className="h-12 bg-black border-t border-white/5 flex items-center justify-center gap-2">
                              {demoSteps.map((step, idx) => (
                                  <div 
                                    key={idx} 
                                    onClick={() => setDemoStep(idx)}
                                    className={`
                                        h-1.5 rounded-full transition-all duration-500 cursor-pointer
                                        ${demoStep === idx ? 'w-8 bg-white' : 'w-1.5 bg-white/20 hover:bg-white/40'}
                                    `}
                                    title={step.title}
                                  />
                              ))}
                          </div>
                      </div>
                      
                      {/* Caption */}
                      <div className="text-center mt-8 h-8">
                          <p className="text-sm font-medium text-zinc-400 animate-in fade-in key={demoStep}">
                              <span className="text-indigo-400 font-bold mr-2">Step {demoStep + 1}:</span> 
                              {demoSteps[demoStep].title} — {demoSteps[demoStep].desc}
                          </p>
                      </div>

                      <div className="absolute -inset-4 bg-indigo-500/20 blur-3xl -z-10 rounded-[100px] opacity-20 group-hover:opacity-40 transition-opacity duration-1000" />
                  </div>
              </div>

              {/* Features Grid */}
              <div id="features" className="relative z-10 py-32 bg-black/50 border-t border-white/5 w-full">
                  <div className="max-w-6xl mx-auto px-6">
                      <div className="text-center mb-16">
                          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Built for the modern founder</h2>
                          <p className="text-sm text-zinc-400 max-w-2xl mx-auto leading-relaxed">Everything you need to go from zero to one. <br />Integrated into a single, seamless workflow.</p>
                      </div>

                      <div className="grid md:grid-cols-3 gap-6">
                          <div className="p-8 rounded-3xl bg-zinc-900/30 border border-white/5 hover:border-white/10 transition-colors group">
                              <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center mb-6 text-purple-400 group-hover:scale-110 transition-transform">
                                  <Cpu size={24} />
                              </div>
                              <h3 className="text-lg font-semibold text-white mb-3">AI Research Engine</h3>
                              <p className="text-sm text-zinc-400 leading-relaxed">
                                  Deep dive analysis on competitors, market size, and user personas instantly. 
                                  Forget spending weeks on Google.
                              </p>
                          </div>
                          <div className="p-8 rounded-3xl bg-zinc-900/30 border border-white/5 hover:border-white/10 transition-colors group">
                              <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-6 text-indigo-400 group-hover:scale-110 transition-transform">
                                  <Palette size={24} />
                              </div>
                              <h3 className="text-lg font-semibold text-white mb-3">Tangible Design</h3>
                              <p className="text-sm text-zinc-400 leading-relaxed">
                                  Generate logos, landing pages, and product mockups automatically. 
                                  Make your ideas feel real enough to touch.
                              </p>
                          </div>
                          <div className="p-8 rounded-3xl bg-zinc-900/30 border border-white/5 hover:border-white/10 transition-colors group">
                              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-6 text-emerald-400 group-hover:scale-110 transition-transform">
                                  <LineChart size={24} />
                              </div>
                              <h3 className="text-lg font-semibold text-white mb-3">Strategic Validation</h3>
                              <p className="text-sm text-zinc-400 leading-relaxed">
                                  Get Sequoia-style pitch decks, business model canvases, and 
                                  go-to-market strategies generated in seconds.
                              </p>
                          </div>
                      </div>
                  </div>
              </div>

              {/* Manifesto Section */}
              <div id="manifesto" className="relative z-10 py-32 border-t border-white/5 overflow-hidden w-full">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-[120px] pointer-events-none opacity-20" />
                  <div className="max-w-4xl mx-auto px-6 text-center relative z-20">
                      <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-8">Manifesto</h2>
                      <h3 className="text-3xl md:text-5xl font-serif italic text-zinc-300 leading-tight mb-8">
                          "Great ideas are fragile. They need a home."
                      </h3>
                      <p className="text-lg text-zinc-500 leading-relaxed max-w-2xl mx-auto">
                          Most ideas die in the Notes app. They starve from lack of clarity, visualization, and validation. 
                          Idea Shelf exists to give your imagination the infrastructure it deserves. We believe that if you can see it, you can build it.
                      </p>
                  </div>
              </div>

              {/* Pricing Section */}
              <div id="pricing" className="relative z-10 py-32 bg-zinc-950/50 border-t border-white/5 w-full">
                  <div className="max-w-7xl mx-auto px-6">
                      <div className="text-center mb-16">
                          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Simple, transparent pricing</h2>
                          <p className="text-sm text-zinc-400">Start for free, scale when you're ready.</p>
                      </div>

                      <div className="grid md:grid-cols-4 gap-6">
                            {/* Free */}
                            <div className="p-6 rounded-2xl border border-white/5 bg-zinc-900/50 hover:border-white/20 transition-all flex flex-col opacity-80 hover:opacity-100">
                                <h3 className="text-lg font-semibold text-white mb-2">Free</h3>
                                <div className="text-3xl font-bold text-white mb-6">$0</div>
                                <div className="space-y-3 mb-8 flex-1">
                                    <div className="flex items-center gap-3 text-sm text-zinc-400"><Check size={16} className="text-zinc-600" /> Basic AI (Flash)</div>
                                    <div className="flex items-center gap-3 text-sm text-zinc-400"><Check size={16} className="text-zinc-600" /> 10k Tokens</div>
                                    <div className="flex items-center gap-3 text-sm text-zinc-400"><Check size={16} className="text-zinc-600" /> Public Ideas Only</div>
                                </div>
                                <button onClick={login} className="w-full py-3 rounded-xl text-sm font-semibold bg-white/5 hover:bg-white/10 text-white transition-colors">Start Free</button>
                            </div>

                            {/* Starter */}
                            <div className="p-6 rounded-2xl border border-white/5 bg-zinc-900/50 hover:border-white/20 transition-all flex flex-col">
                                <h3 className="text-lg font-semibold text-white mb-2">Starter</h3>
                                <div className="text-3xl font-bold text-white mb-6">$12<span className="text-sm font-normal text-zinc-500">/mo</span></div>
                                <div className="space-y-3 mb-8 flex-1">
                                    <div className="flex items-center gap-3 text-sm text-zinc-300"><Check size={16} className="text-white" /> Basic AI (Flash)</div>
                                    <div className="flex items-center gap-3 text-sm text-zinc-300"><Check size={16} className="text-white" /> 500k Credits</div>
                                    <div className="flex items-center gap-3 text-sm text-zinc-300"><Check size={16} className="text-white" /> Private Ideas</div>
                                </div>
                                <button onClick={login} className="w-full py-3 rounded-xl text-sm font-semibold bg-white text-black hover:bg-zinc-200 transition-colors">Get Starter</button>
                            </div>

                            {/* Pro */}
                            <div className="p-6 rounded-2xl border border-indigo-500/30 bg-indigo-500/5 hover:border-indigo-500/50 transition-all flex flex-col relative ring-1 ring-indigo-500/20">
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-indigo-500 text-white text-[10px] font-bold uppercase tracking-wider">Most Popular</div>
                                <h3 className="text-lg font-semibold text-indigo-300 mb-2">Pro</h3>
                                <div className="text-3xl font-bold text-white mb-6">$29<span className="text-sm font-normal text-zinc-500">/mo</span></div>
                                <div className="space-y-3 mb-8 flex-1">
                                    <div className="flex items-center gap-3 text-sm text-zinc-300"><Check size={16} className="text-indigo-400" /> Advanced AI (Pro)</div>
                                    <div className="flex items-center gap-3 text-sm text-zinc-300"><Check size={16} className="text-indigo-400" /> 2.5M Credits</div>
                                    <div className="flex items-center gap-3 text-sm text-zinc-300"><Check size={16} className="text-indigo-400" /> Image Generation</div>
                                    <div className="flex items-center gap-3 text-sm text-zinc-300"><Check size={16} className="text-indigo-400" /> Priority Support</div>
                                </div>
                                <button onClick={login} className="w-full py-3 rounded-xl text-sm font-semibold bg-indigo-500 text-white hover:bg-indigo-400 transition-colors shadow-lg shadow-indigo-500/20">Get Pro</button>
                            </div>

                             {/* Executive */}
                             <div className="p-6 rounded-2xl border border-white/5 bg-zinc-900/50 hover:border-white/20 transition-all flex flex-col">
                                <h3 className="text-lg font-semibold text-white mb-2">Executive</h3>
                                <div className="text-3xl font-bold text-white mb-6">$49<span className="text-sm font-normal text-zinc-500">/mo</span></div>
                                <div className="space-y-3 mb-8 flex-1">
                                    <div className="flex items-center gap-3 text-sm text-zinc-300"><Check size={16} className="text-white" /> Advanced AI (Pro)</div>
                                    <div className="flex items-center gap-3 text-sm text-zinc-300"><Check size={16} className="text-white" /> 6M Credits</div>
                                    <div className="flex items-center gap-3 text-sm text-zinc-300"><Check size={16} className="text-white" /> Image Generation</div>
                                    <div className="flex items-center gap-3 text-sm text-zinc-300"><Check size={16} className="text-white" /> Priority Support</div>
                                </div>
                                <button onClick={login} className="w-full py-3 rounded-xl text-sm font-semibold bg-white/5 hover:bg-white/10 text-white transition-colors">Contact Sales</button>
                            </div>
                      </div>
                  </div>
              </div>

              {/* Footer */}
              <footer className="py-12 border-t border-white/5 bg-black text-xs text-zinc-500 w-full">
                  <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
                      <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded bg-zinc-800 flex items-center justify-center text-[10px] text-white font-bold">IS</div>
                          <span className="font-semibold text-zinc-300">Idea Shelf</span>
                          <span className="ml-2">© 2025</span>
                      </div>
                      <div className="flex gap-6">
                          <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
                          <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                          <Link to="/security" className="hover:text-white transition-colors">Security</Link>
                      </div>
                  </div>
              </footer>
          </div>
    );
};

// --- Main App Component ---
const IdeaShelf: React.FC = () => {
  const { user, isGuest, isAuthLoading, initializeAuth, userProfile, ideas } = useStore();

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 animate-pulse">
            <div className="w-12 h-12 rounded bg-zinc-800 flex items-center justify-center text-zinc-500 font-bold text-xl">IS</div>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Header />
      <div className="pt-20 min-h-screen bg-black">
        <Suspense fallback={
            <div className="min-h-screen bg-black text-white flex items-center justify-center">
                <Loader2 className="animate-spin text-zinc-500" size={32} />
            </div>
        }>
            <Routes>
                <Route path="/" element={user || isGuest ? <Shelf 
                    ideas={ideas} 
                    onSelectIdea={(idea) => useStore.getState().selectIdea(idea)} 
                    onCreateIdea={() => {}}
                    onCompare={(ids) => useStore.getState().setComparisonIds(ids)}
                /> : <LandingPage />} />
                
                <Route path="/explore" element={<Explore />} />
                <Route path="/idea/:id" element={user || isGuest ? <IdeaEditor onClose={() => {}} /> : <Navigate to="/" />} />
                
                {/* Legal Pages */}
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/security" element={<SecurityPage />} />

                {/* Catch-all */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
      </div>

      {/* Modals Layer */}
      <PricingModal />
      {/* Strict check: Ensure userProfile is loaded before evaluating onboarding status to prevent flashing */}
      {user && userProfile && !userProfile.onboardingCompleted && <OnboardingModal />}
      
      {/* Comparison View Overlay */}
      {useStore.getState().comparisonIds.length > 0 && (
          <ComparisonView 
            ideas={ideas.filter(i => useStore.getState().comparisonIds.includes(i.id))}
            onClose={() => useStore.getState().setComparisonIds([])}
          />
      )}
    </BrowserRouter>
  );
};

export default IdeaShelf;